import { format } from 'date-fns';
import { Lightbulb, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmployeeAvatar } from '@/components/EmployeeAvatar';
import type { Anomaly } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { Timesheet } from '@/types/timesheet';
import { StageBadge } from './StageBadge';
import { WaitingBadge } from './WaitingBadge';
import type { TimesheetReviewTableProps } from './TimesheetReviewTable';
import { TimesheetReviewTable } from './TimesheetReviewTable';
import type { TimesheetReviewFooterProps } from './TimesheetReviewFooter';
import { TimesheetReviewFooter } from './TimesheetReviewFooter';

export interface TimesheetReviewDialogProps {
  timesheet: Timesheet | null;
  onOpenChange: (open: boolean) => void;
  photoUrl?: string;
  /** Violations the current user is permitted to see. */
  violations: PolicyViolation[];
  /** Anomalies the current user is permitted to see. */
  anomalies: Anomaly[];
  onShowIssues: () => void;
  isEditMode: boolean;
  canEdit: boolean;
  onStartEdit: () => void;
  tableProps: Omit<TimesheetReviewTableProps, 'timesheet'>;
  footerProps: Omit<TimesheetReviewFooterProps, 'timesheet'>;
}

export function TimesheetReviewDialog({
  timesheet,
  onOpenChange,
  photoUrl,
  violations,
  anomalies,
  onShowIssues,
  isEditMode,
  canEdit,
  onStartEdit,
  tableProps,
  footerProps,
}: TimesheetReviewDialogProps) {
  const totalIssues = violations.length + anomalies.length;
  const hasHigh = violations.some(v => v.severity === 'high') || anomalies.some(a => a.severity === 'high');
  const hasMedium = violations.some(v => v.severity === 'medium') || anomalies.some(a => a.severity === 'medium');
  const issuesButtonColour = hasHigh
    ? 'border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30'
    : hasMedium
    ? 'border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950/30'
    : 'border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-950/30';

  return (
    <Dialog open={!!timesheet} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            {timesheet && (
              <EmployeeAvatar
                name={timesheet.employeeName}
                photoUrl={photoUrl}
                size="lg"
              />
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="truncate min-w-0 flex-shrink">Timesheet - {timesheet?.employeeName}</span>
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto mr-6">
              {timesheet && totalIssues > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onShowIssues}
                  className={`h-7 px-2.5 text-xs font-medium gap-1.5 ${issuesButtonColour}`}
                >
                  <Lightbulb className="h-3.5 w-3.5" />
                  Issues ({totalIssues})
                </Button>
              )}
              {isEditMode && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200">
                  Editing
                </Badge>
              )}
              {canEdit && timesheet?.allowApproverEdit === true && !isEditMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onStartEdit}
                  className="shrink-0"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Timesheet
                </Button>
              )}
              </div>
            </div>
          </DialogTitle>
          <DialogDescription className="ml-[60px] flex items-center gap-3 flex-wrap">
            <span>Week ending {timesheet && format(timesheet.weekEnding, 'dd MMM yyyy')}</span>
            {timesheet?.currentStageName && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <StageBadge
                  name={timesheet.currentStageName}
                  order={timesheet.currentStageOrder}
                  total={timesheet.totalStages}
                />
              </>
            )}
            {timesheet?.submittedAt ? (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="text-muted-foreground text-sm">
                  Submitted {format(timesheet.submittedAt, 'dd MMM yyyy')}
                </span>
                <WaitingBadge submittedAt={timesheet.submittedAt} />
              </>
            ) : (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="text-muted-foreground text-sm">Submission date unknown</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1">
          {isEditMode && (
            <div className="sticky top-0 z-10 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 font-medium shadow-sm">
              <span>✏️ Edit mode active — make your changes then click <strong>Save Changes</strong> in the footer.</span>
            </div>
          )}
          <TimesheetReviewTable timesheet={timesheet} {...tableProps} />
        </div>
        <TimesheetReviewFooter timesheet={timesheet} {...footerProps} />
      </DialogContent>
    </Dialog>
  );
}
